<template>
    <div class="h-100">
        <vue-progress-bar/>

        <div v-if="isTeamSetup || isTeamRequiredDataUpdating" class="pt-3 text-muted text-center font-18">
            Setup {{ activeTeam ? activeTeam.name : '' }}...
        </div>
        <template v-else>
            <BillingStatuses/>
            <HotkeysSidePanel v-if="$breakpoint.mdAndUp"/>

            <div class="flexFrame">

                <Header/>

                <div class="flexBody">

                    <button v-if="showSidebarSwitcher"
                            @click="toggleSidebar"
                            class="mobile-sidebar-btn btn btn-primary btn-block rounded-0 mt-0">
                        <i class="fa fa-sliders-v"></i>
                    </button>

                    <div ref="flexSidebar" class="flexSidebar">

                        <router-view name="sidebar"/>

                    </div>
                    <div class="flexContent hide-scrollbar"
                         :class="{ 'with-toolbar-switcher': showSidebarSwitcher }">

                        <router-view/>

                    </div>

                </div>
            </div>
        </template>

        <modals-container/>

        <AutomationCenter/>
        <SelectActiveTeam name="select-active-team"/>
        <ChangePassword name="change-password"/>
        <!-- 2fa -->
        <SetupTwoFactor name="setup-two-factor"/>
        <RemoveTwoFactor name="remove-two-factor"/>
        <SuccessfulTwoFactorEnabling name="successful-two-factor-enabling"/>
        <!-- billing -->
        <EditBillingCard name="edit-billing-card"/>
        <UpdateBillingAlert name="billing-required-alert"/>
        <ChangeBillingPlan name="change-billing-plan"/>
        <ContactUsToUpdateBillingPlan name="contact-us-to-update-billing-plan"/>

        <NoInbox name="no-inbox"/>
        <CreateDocument name="create-document"/>
        <DocTypeSettingsModal name="doc-type-settings"/>
        <DocSubtypeSettingsModal name="doc-subtype-settings-subtype"/>
        <ConfirmActionModal/>
        <AccountPasswordConfirmationModal/>
    </div>
</template>

<script>
import {mapActions, mapGetters, mapMutations} from 'vuex'
import EventBus from '../command/bus'
import SelectActiveTeam from '../modals/shared/SelectActiveTeam'

import {
    AUTH_FORCE_2FA_SETUP,
    AUTH_REFRESH_DATA,
    AUTH_SET_ACTIVE_TEAM,
    AUTH_SET_STAFF_ACCESS_TOKEN,
    AUTH_USER_PERMISSIONS_GET,
    MEMBERS_PRESENCE_SET,
    ONBOARDING_REFRESH,
    TEAMS_STATS_FETCH,
    USER_FETCH_LIST
} from '@/store/mutations'
import {DOCS_TYPES_FETCH} from '@/store/modules/documents'
import {
    DATA_FETCH_CURRENCIES,
    DATA_FETCH_ENTITIES,
    DATA_FETCH_LOCATIONS,
    DATA_FETCH_SUBSIDIARIES,
    DATA_FETCH_TEAM_DEPENDENT
} from '@/store/modules/app/data'
import {INBOX_FETCH_COUNTERS, INBOX_FETCH_LIST, INBOX_FETCH_STATUS} from '@/store/modules/inbox'
import {ERROR, INFO, SUCCESS} from '@/store/modules/ui/UI'
import {BILLING_INFO_FETCH} from '@/store/modules/billing'
import HotkeysSidePanel from '../components/hotkeys/HotkeysSidePanel'
import Header from '../components/header/Header'
import NoInbox from '../modals/inbox/NoInbox'
import CreateDocument from '../views/documents/single/upload/CreateDocument'
import ConfirmActionModal from '../decorators/withConfirmation/ConfirmActionModal'
import BillingStatuses from '../modals/settings/team/billing/BillingStatuses'
import EditBillingCard from '../modals/settings/team/billing/EditBillingCard'
import ContactUsToUpdateBillingPlan from '../modals/settings/team/billing/ContactUsToUpdateBillingPlan'
import UpdateBillingAlert from '../decorators/withBillingRequirements/UpdateBillingAlert'
import ChangeBillingPlan from '../modals/settings/team/billing/ChangeBillingPlan'
import SetupTwoFactor from '../modals/settings/profile/SetupTwoFactor'
import RemoveTwoFactor from '../modals/settings/profile/RemoveTwoFactor'
import SuccessfulTwoFactorEnabling from '../modals/settings/profile/SuccessfulTwoFactorEnabling'
import {
    SIDEBAR_SET_SELECTED_CONTACT,
    SIDEBAR_SET_SELECTED_LOCATION_UUID,
    SIDEBAR_SET_SELECTED_MEMBER_UUID,
    SIDEBAR_SET_SELECTED_SUBSIDIARY_UUID,
    SIDEBAR_SET_SELECTED_SUBTYPE
} from '@/store/modules/app/sidebar'
import DocSubtypeSettingsModal from '../views/documents/subtypes/settings/DocSubtypeSettingsModal'
import DocTypeSettingsModal from '../views/documents/types/settings/DocTypeSettingsModal'
import AutomationCenter from '../views/automation/AutomationCenter'
import ChangePassword from '../modals/settings/profile/ChangePassword'
import {AppPusherTeamEvents, getTeamEventsPusherChannel} from '@/pusher/channels/events/team'
import {
    subscribeToTeamMembersPresenceChannel,
    unsubscribeFromTeamMembersPresenceChannel
} from '@/pusher/channels/presence/team'
import {AppPusherService} from '@/pusher'
import {openDocumentViewer} from '@/views/documents/single/view'
import {AppDocSystemTypes, docSystemTypesDefaultModels} from '@/types/document/types'
import {appHelpChat, loadAppHelpChatScript} from '@/services/chats'
import AccountPasswordConfirmationModal from '@/decorators/password-confirmation/AccountPasswordConfirmationModal.vue'
import {initializePendoIoTracker} from '@/setup/pendo-io-tracker'
import {documentApi} from '@/api/document'
import {showErrorMessage} from '@/services/snackbar'
import {get} from 'lodash'
import axios from 'axios'
import {demoContractApi} from '@/api/document/demo-contracts'
import moment from 'moment'

export default {
    components: {
        AccountPasswordConfirmationModal,
        AutomationCenter,
        ChangePassword,
        DocTypeSettingsModal,
        DocSubtypeSettingsModal,
        SuccessfulTwoFactorEnabling,
        RemoveTwoFactor,
        SetupTwoFactor,
        ChangeBillingPlan,
        UpdateBillingAlert,
        ContactUsToUpdateBillingPlan,
        EditBillingCard,
        BillingStatuses,
        ConfirmActionModal,
        CreateDocument,
        NoInbox,
        Header,
        HotkeysSidePanel,
        SelectActiveTeam
    },
    data() {
        return {
            prevActiveTeamUuid: null,
            isTeamSetup: false,
            isTeamRequiredDataUpdating: false
        }
    },
    computed: {
        ...mapGetters([
            'getToken',
            'activeTeam',
            'activeTeamId',
            'getUser',
            'getTeams',
            'selectedLocationUuid',
            'selectedSubsidiaryUuid',
            'fullName',
            'isStaff',
            'isAuthenticated',
            'hasAllOnboardingStepsPassed',
            'hasFirstUploadedInvoice',
            'hasInvitedMember',
            'membersPresence',
            'membersAvatars',
            'userLocations',
            'userSubsidiaries'
        ]),

        ...mapGetters('documents', [
            'documentTypeSelected'
        ]),

        showSidebarSwitcher() {
            return this.$breakpoint.smAndDown &&
                [
                    docSystemTypesDefaultModels.purchasing.route,
                    docSystemTypesDefaultModels.sales.route,
                    docSystemTypesDefaultModels.statements.route,
                    docSystemTypesDefaultModels.documents.route,
                    'settings', 'reports'
                ].includes(this.$route.name)
        }
    },
    methods: {
        ...mapActions({
            getUserPermissions: AUTH_USER_PERMISSIONS_GET,
            authSetStaffAccessToken: AUTH_SET_STAFF_ACCESS_TOKEN,
            fetchTeamDependentData: DATA_FETCH_TEAM_DEPENDENT,
            fetchLocations: DATA_FETCH_LOCATIONS,
            fetchSubsidiaries: DATA_FETCH_SUBSIDIARIES,
            fetchCurrencies: DATA_FETCH_CURRENCIES,
            fetchUsers: USER_FETCH_LIST,
            fetchTeamsStats: TEAMS_STATS_FETCH,
            force2faSetup: AUTH_FORCE_2FA_SETUP,
            fetchOnboarding: ONBOARDING_REFRESH
        }),

        ...mapMutations({
            setSelectedContact: SIDEBAR_SET_SELECTED_CONTACT,
            setSelectedSubtype: SIDEBAR_SET_SELECTED_SUBTYPE,
            setSelectedMemberUuid: SIDEBAR_SET_SELECTED_MEMBER_UUID,
            setSelectedLocationUuid: SIDEBAR_SET_SELECTED_LOCATION_UUID,
            setSelectedSubsidiaryUuid: SIDEBAR_SET_SELECTED_SUBSIDIARY_UUID,
            setActiveTeam: AUTH_SET_ACTIVE_TEAM,
            setMembersPresence: MEMBERS_PRESENCE_SET
        }),

        ...mapActions('billing', {
            fetchBillingInfo: BILLING_INFO_FETCH
        }),

        ...mapActions('documents', {
            fetchDocTypes: DOCS_TYPES_FETCH
        }),

        ...mapActions('inbox', {
            fetchInboxes: INBOX_FETCH_LIST,
            fetchInboxStatus: INBOX_FETCH_STATUS,
            fetchInboxCounters: INBOX_FETCH_COUNTERS
        }),

        async createDocument({documentType, onSuccessUpload}) {
            if (!documentType) {
                documentType = this.documentTypeSelected
            }
            if (!documentType) {
                this.$snackbar.show({type: INFO, text: 'No document type specified'})
                return
            }
            const locationId = this.getLocationIdToUploadDocument()
            const subsidiaryId = this.getSubsidiaryIdToUploadDocument()

            const documentTypeKey = get(documentType, 'type')

            const isDemoItems = documentTypeKey === AppDocSystemTypes.DEMO_ITEMS
            const isDemoSuppliers = documentTypeKey === AppDocSystemTypes.DEMO_SUPPLIERS
            const isDemoContracts = documentTypeKey === AppDocSystemTypes.DEMO_CONTRACTS

            if (documentType.hasDocument) {
                this.$modal.show('create-document', {
                    documentType,
                    onSuccessUpload,
                    locationId,
                    subsidiaryId
                })
            } else if (isDemoItems || isDemoSuppliers) {
                const {error, data} = await documentApi.createDocWithoutAttachments({
                    type: documentTypeKey
                })
                if (error) {
                    showErrorMessage('Failed to create document')
                } else {
                    await openDocumentViewer({id: data.uuid})
                }
            } else if (isDemoContracts) {
                const {error, data} = await demoContractApi.createOrUpdate({
                    name: '',
                    type: 'DEFAULT',
                    status: 'ACTIVE',
                    date_start: moment().format('YYYY-MM-DD'),
                    date_end: moment().add(1, 'month').format('YYYY-MM-DD'),
                    supplier: '',
                    lines: [{
                        item: '',
                        qty: 1,
                        price: 100,
                        location: null,
                        subsidiary: null
                    }]
                })
                if (error) {
                    showErrorMessage('Failed to create a contract')
                } else {
                    openDocumentViewer({id: data.uuid})
                }
            } else {
                axios.post(`documents/types/${documentType.uuid}/documents/create`, {
                    documentType: documentType.uuid,
                    branch: locationId,
                    subsidiary: subsidiaryId
                })
                    .then(res => {
                        this.$snackbar.show({type: SUCCESS, text: 'Document created'})
                        this.$bus.$emit('DOCUMENT_CREATED', res.data.data)
                        openDocumentViewer({id: res.data.data.uuid})
                    })
                    .catch(error => {
                        this.$snackbar.show({error})
                    })
            }
        },

        getLocationIdToUploadDocument() {
            let locationId = this.selectedLocationUuid
            if (!locationId && this.userLocations.length === 1) {
                locationId = _.get(this.userLocations, [0, 'uuid'])
            }
            return locationId
        },

        getSubsidiaryIdToUploadDocument() {
            let subsidiaryId = this.selectedSubsidiaryUuid
            if (!subsidiaryId && this.userSubsidiaries.length === 1) {
                subsidiaryId = _.get(this.userSubsidiaries, [0, 'uuid'])
            }
            return subsidiaryId
        },

        refreshSuppliers() {
            EventBus.$emit('REFRESH_SUPPLIER')
        },

        refreshEntities() {
            this.$store.dispatch(DATA_FETCH_ENTITIES)
        },

        refreshUsers() {
            this.fetchUsers()
            if (!this.hasInvitedMember) {
                this.refreshOnboarding()
            }
        },

        async refreshAuthData() {
            await this.$store.dispatch(AUTH_REFRESH_DATA)
            if (!this.hasInvitedMember) {
                this.refreshOnboarding()
            }
        },

        refreshOnboarding() {
            if (!this.hasAllOnboardingStepsPassed) {
                this.fetchOnboarding()
            }
        },

        showSidebar() {
            if (this.$refs.flexSidebar) {
                this.$refs.flexSidebar.style.left = '0px'
            }
        },

        hideSidebar() {
            if (this.$refs.flexSidebar) {
                this.$refs.flexSidebar.style.left = '-100vw'
            }
        },

        toggleSidebar() {
            if (this.$refs.flexSidebar) {
                if (!this.$refs.flexSidebar.style.left ||
                    this.$refs.flexSidebar.style.left === '0px') {
                    this.$refs.flexSidebar.style.left = '-100vw'
                } else {
                    this.$refs.flexSidebar.style.left = '0px'
                }
            }
        },

        handleWorkflowAuthConnectionStatusChange(data) {
            EventBus.$emit('WORKFLOW_AUTH_CONNECTION_STATUS_CHANGED', data)
        },

        subscribeToTeamMembersPresenceChannel() {
            unsubscribeFromTeamMembersPresenceChannel(this.prevActiveTeamUuid)
            subscribeToTeamMembersPresenceChannel(this.activeTeamId)
        },

        subscribeToActiveTeamEvents() {
            this.unsubscribeFromTeamEvents(this.prevActiveTeamUuid)
            const channel = getTeamEventsPusherChannel(this.activeTeamId)
            channel.unbind_all()
            channel.bind(AppPusherTeamEvents.USER_CREATED, this.refreshUsers)
            channel.bind(AppPusherTeamEvents.USER_UPDATED, this.refreshUsers)
            channel.bind(AppPusherTeamEvents.USER_DELETED, this.refreshUsers)
            channel.bind(AppPusherTeamEvents.TEAM_UPDATED, this.refreshAuthData)
            channel.bind(AppPusherTeamEvents.SUPPLIER_CREATED, this.refreshSuppliers)
            channel.bind(AppPusherTeamEvents.LOCATION_CREATED, this.fetchLocations)
            channel.bind(AppPusherTeamEvents.LOCATION_UPDATED, this.fetchLocations)
            channel.bind(AppPusherTeamEvents.LOCATION_DELETED, this.fetchLocations)
            channel.bind(AppPusherTeamEvents.SUBSIDIARY_CREATED, this.fetchSubsidiaries)
            channel.bind(AppPusherTeamEvents.SUBSIDIARY_UPDATED, this.fetchSubsidiaries)
            channel.bind(AppPusherTeamEvents.SUBSIDIARY_DELETED, this.fetchSubsidiaries)
            // TODO: move all event below to place where they are used
            channel.bind(AppPusherTeamEvents.NOTIFICATION, data => {
                if (data && data.user === this.getUser.uuid) {
                    // TODO: fetch notification count only
                }
            })
            channel.bind(AppPusherTeamEvents.CONNECTION_CONNECTED, this.handleWorkflowAuthConnectionStatusChange)
            channel.bind(AppPusherTeamEvents.CONNECTION_FAILED, this.handleWorkflowAuthConnectionStatusChange)
            channel.bind(AppPusherTeamEvents.CONNECTION_RECONNECT, this.handleWorkflowAuthConnectionStatusChange)
            channel.bind(AppPusherTeamEvents.CONNECTION_REDIRECT, this.handleWorkflowAuthConnectionStatusChange)
            channel.bind(AppPusherTeamEvents.WORKFLOW_EVENT, e => {
                this.$bus.$emit('WORKFLOW_EVENT', e)
            })
            channel.bind(AppPusherTeamEvents.CSV_IMPORT_COMPLETE, e => {
            })
            channel.bind(AppPusherTeamEvents.DATASET_UPDATED, uuid => {
                this.$bus.$emit('DATASET_UPDATED', uuid)
            })
            channel.bind(AppPusherTeamEvents.INBOX_MESSAGE, message => {
                this.fetchInboxCounters()
                this.$bus.$emit('INBOX_MESSAGE', message)
            })
            channel.bind(AppPusherTeamEvents.APPROVAL_UPDATED, approval => {
                this.fetchInboxCounters()
                this.$bus.$emit('APPROVAL_UPDATED', approval)
            })
        },

        unsubscribeFromTeamEvents(teamId) {
            if (!teamId) {
                return
            }
            const channel = getTeamEventsPusherChannel(teamId)
            if (channel) {
                channel.unbind_all()
            }
        },

        clearPrevTeamData() {
            this.setSelectedContact(null)
            this.setSelectedSubtype(null)
            this.setSelectedMemberUuid(null)
            this.setSelectedLocationUuid(null)
            this.setSelectedSubsidiaryUuid(null)
        },

        async setupTeamData() {
            this.isTeamRequiredDataUpdating = true
            this.$Progress.start()
            this.clearPrevTeamData()
            await Promise.all([ // required data
                this.fetchLocations(),
                this.fetchSubsidiaries(),
                this.fetchDocTypes(),
                this.fetchBillingInfo(),
                this.refreshAuthData()
            ])
                .catch(e => {
                    this.$raven.captureException(e)
                })
                .finally(() => {
                    this.isTeamRequiredDataUpdating = false
                    this.$Progress.finish()
                })

            if (this.activeTeam.force2fa) {
                this.force2faSetup()
            }
            // not required for first load:
            this.fetchTeamsStats()
            this.fetchTeamDependentData()
            this.fetchOnboarding()

            this.fetchInboxes()
            this.fetchInboxStatus()
            this.fetchInboxCounters()
        },

        setupTeamPusher() {
            AppPusherService.init({
                team: this.activeTeamId,
                token: this.getToken,
                debug: false
            })
            this.subscribeToActiveTeamEvents()
            this.subscribeToTeamMembersPresenceChannel()
        },

        async loadAppHelpChat() {
            try {
                await loadAppHelpChatScript()
                appHelpChat.boot()
            } catch (e) {
                this.$raven.captureException('Failed to load app help chat script')
            }
        }
    },
    created() {
        this.fetchCurrencies()
        // ACTIVE TEAM re-setup + required data update
        // (Cross watch with route team for correct switch active team on open url belonging to another team)
        this.$watch(vm => [vm.activeTeam, vm.$route.params.team], async() => {
            if (this.$route.params.team && this.activeTeamId !== this.$route.params.team) {
                this.isTeamSetup = true
                const team = this.getTeams.find(el => el.uuid === this.$route.params.team)
                if (team) {
                    await this.$snackbar.show({
                        type: SUCCESS,
                        text: `Switching active team to "${team.name}"...`,
                        duration: 2000
                    })
                    this.setActiveTeam(team)
                    this.isTeamSetup = false
                } else {
                    if (this.isStaff) {
                        await this.$snackbar.show({
                            type: INFO,
                            text: `Getting access to ID:${this.$route.params.team} team`
                        })
                        const token = this.authSetStaffAccessToken(this.$route.params.team)
                        await this.$snackbar.show(token ? {type: SUCCESS, text: `Access granted`} : {type: ERROR})
                    } else if (this.isAuthenticated) {
                        await this.$snackbar.show({
                            type: INFO,
                            text: `You probably don't have access to ID:${this.$route.params.team} team`
                        })
                    }
                }
            } else if (this.activeTeamId !== this.prevActiveTeamUuid) {
                this.prevActiveTeamUuid = this.activeTeamId
                this.setupTeamPusher()
                await this.setupTeamData()
                this.isTeamSetup = false
                await this.$router.push({
                    name: this.$route.name,
                    params: {...this.$route.params, team: this.activeTeamId},
                    query: this.$route.query
                }).catch(() => {
                }) // navigation duplicate catch
            }
        }, {deep: true, immediate: true})
    },
    mounted() {
        this.loadAppHelpChat()
        this.$bus.$on('REFRESH_ONBOARDING', this.refreshOnboarding)
        this.$bus.$on('DOCUMENT_ADD', this.createDocument)
        this.$bus.$on('TOGGLE_SIDEBAR', val => {
            if (val) {
                this.showSidebar()
            } else {
                this.hideSidebar()
            }
        })
        if (this.getUser.forcePasswordChange) {
            this.$modal.show('change-password', {canSkip: false})
        }
    },
    beforeDestroy() {
        AppPusherService.destroy()
        appHelpChat.shutdown()
        this.$bus.$off('REFRESH_ONBOARDING')
        this.$bus.$off('DOCUMENT_ADD')
        this.$bus.$off('OPEN_PROGRESS_DROPDOWN')
        this.$bus.$off('TOGGLE_SIDEBAR')
    },
    watch: {
        $breakpoint(val) {
            if (val.smAndUp) {
                this.showSidebar()
            } else {
                this.hideSidebar()
            }
        },
        getToken: {
            handler() {
                this.getUserPermissions()
            },
            deep: true,
            immediate: true
        },
        activeTeamId: {
            handler(teamId) {
                if (!teamId) {
                    return
                }
                initializePendoIoTracker({user: this.getUser, team: this.activeTeam})
            },
            immediate: true
        }
    }
}
</script>

<style lang="scss">
.mobile-sidebar-btn {
    position: fixed;
    z-index: 3;
    top: 60px;
    left: 0;
}
</style>
